import { z } from 'zod'

/**
 * Reports & Analytics contracts. These Zod schemas are the single source of
 * truth for the "Performa Bisnis" feature: the API layer validates the raw
 * payload with them, and the UI model derives its types via `z.infer`.
 *
 * Backend contract: WEBLP-3265, `GET /v1/algo-pos-be/liotitip/analytics`.
 */

/** Tabs of the "Periode performa" selector. UI-only; the API never sees it. */
export const performancePeriodSchema = z.enum(['daily', 'weekly', 'monthly'])

/** The four headline metrics shown as selectable cards above the chart. */
export const performanceMetricIdSchema = z.enum(['revenue', 'orders', 'aov', 'buyers'])

/** A single bar in the chart: an x-axis label and its numeric value. */
export const performancePointSchema = z.object({
  label: z.string(),
  value: z.number(),
})

export const performanceMetricSchema = z.object({
  id: performanceMetricIdSchema,
  label: z.string(),
  /** Formatted display value for the metric card, e.g. "Rp12.480.000". */
  display: z.string(),
  /** Period-over-period change, e.g. "+9,7%", or "–" when there is no baseline. */
  delta: z.string(),
  /** `true` = increase, `false` = decrease, `null` = no comparable baseline. */
  deltaPositive: z.boolean().nullable(),
  /** Chart title shown above the chart when this metric is active. */
  chartTitle: z.string(),
  /** Whether chart values are currency (formats axis/tooltip as Rupiah). */
  currency: z.boolean(),
  series: z.array(performancePointSchema),
})

export const performanceDatasetSchema = z.object({
  /** Optional caption text; the page derives its caption from the date range. */
  rangeLabel: z.string().optional(),
  metrics: z.array(performanceMetricSchema),
})

export type PerformancePeriod = z.infer<typeof performancePeriodSchema>
export type PerformanceMetricId = z.infer<typeof performanceMetricIdSchema>
export type PerformancePoint = z.infer<typeof performancePointSchema>
export type PerformanceMetric = z.infer<typeof performanceMetricSchema>
export type PerformanceDataset = z.infer<typeof performanceDatasetSchema>

// --- Raw backend contract -----------------------------------------------------
// These schemas mirror the API payload; `toDataset` adapts them into the UI
// model above, so components never touch the snake_case contract.

/** Bucket size. The API accepts only `day`, `week`, and `month` (WEBLP-3265). */
export const reportAggregationSchema = z.enum(['day', 'week', 'month'])

const analyticsTotalsSchema = z.object({
  total_sale: z.number(),
  total_orders: z.number(),
  sale_per_order: z.number(),
  total_customer: z.number(),
})

/** Metric field names shared by `totals`, every bucket, and `change_percent`. */
export type ReportMetricKey = keyof z.infer<typeof analyticsTotalsSchema>

const analyticsBucketSchema = analyticsTotalsSchema.extend({
  /** `YYYY-MM-DD` (week buckets start Monday, month buckets the 1st). */
  date: z.string(),
})

/**
 * `previous` is part of the contract but the UI does not use it yet, so it is
 * intentionally left out: unknown keys are stripped and a change to it cannot
 * break parsing.
 */
export const analyticsResponseSchema = z.object({
  aggregation: reportAggregationSchema,
  current: z.object({
    totals: analyticsTotalsSchema,
    buckets: z.array(analyticsBucketSchema),
  }),
  /** `(current - previous) / previous * 100`, or `null` when previous is zero. */
  change_percent: z.object({
    total_sale: z.number().nullable(),
    total_orders: z.number().nullable(),
    sale_per_order: z.number().nullable(),
    total_customer: z.number().nullable(),
  }),
})

export type ReportAggregation = z.infer<typeof reportAggregationSchema>
export type AnalyticsResponse = z.infer<typeof analyticsResponseSchema>

/** Request params the chart passes to the API. */
export interface PerformanceQuery {
  aggregation: ReportAggregation
  /** `YYYY-MM-DD` of the selected range start (inclusive, Asia/Jakarta). */
  startDate: string
  /** `YYYY-MM-DD` of the selected range end (inclusive, Asia/Jakarta). */
  endDate: string
}
